<template>
  <div class="tr">
    <div class="tr-head">
      <span class="tr-team">{{ label }} Team</span>
      <span class="tr-grade" :class="'g-' + grade.color">{{ grade.grade }}</span>
      <span class="tr-avg">avg unit ~{{ grade.score }}%</span>
    </div>
    <div v-for="u in grade.units" :key="u.name" class="tr-row">
      <div class="tr-line">
        <span class="tr-name">{{ u.name }}</span>
        <span class="tr-cls">{{ u.cls }}</span>
        <span class="tr-spacer"></span>
        <span class="tr-g" :class="'g-' + u.color">{{ u.grade }}</span>
        <span class="tr-wr" :class="u.score >= 50 ? 'up' : 'down'">~{{ u.score }}%</span>
      </div>
      <div class="tr-bar"><span :class="u.score >= 50 ? 'up' : 'down'" :style="barStyle(u.score)"></span></div>
      <div v-if="drivers(u).length" class="tr-chips">
        <span v-for="p in drivers(u)" :key="p.dim" class="tr-chip" :class="p.contribution >= 0 ? 'up' : 'down'">{{ p.label }} {{ signed(p.contribution) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { topParts } from '../lib/unitGrade.js';

// Compact team-rating hover content: per-unit class, a diverging win-rate bar
// (centred on the 50% average), and the unit's biggest grade drivers. Shared by
// the bracket cell badges (BracketMatch) and the team-grid header (Luxstadio).
export default {
  name: 'TeamRatingTip',
  props: {
    grade: { type: Object, required: true }, // a teamGrade() result {score,color,grade,units:[{name,cls,grade,color,score,parts}]}
    label: { type: String, default: '' },
  },
  methods: {
    signed(d) { return (d >= 0 ? '+' : '−') + Math.abs(d).toFixed(1); },
    // Up to 1 best + 1 worst grade driver (the row is tight; 2 chips reads well).
    drivers(u) { const t = topParts(u.parts, 1); return [...t.up, ...t.down]; },
    // Diverging bar: scores cluster ~40–60, so map that span across the track and
    // grow the fill out from the 50% centre — right/green above average, left/red below.
    barStyle(score) {
      const pos = Math.max(0, Math.min(1, (score - 40) / 20)) * 100;
      return score >= 50
        ? { left: '50%', width: (pos - 50) + '%' }
        : { left: pos + '%', width: (50 - pos) + '%' };
    },
  },
};
</script>

<style scoped>
.tr { font-family: inherit; font-size: 12.5px; line-height: 1.35; min-width: 200px; }
.tr-head { display: flex; align-items: center; gap: 7px; padding-bottom: 6px; margin-bottom: 6px; border-bottom: 1px solid rgba(255,255,255,.18); }
.tr-team { font-weight: 700; font-size: 14px; letter-spacing: .3px; }
.tr-avg { margin-left: auto; opacity: .7; font-size: 11px; white-space: nowrap; }
.tr-grade { font-weight: 700; font-size: 12px; padding: 1px 6px; border-radius: 4px; color: #10151b; }

.tr-row { padding: 4px 0; }
.tr-row + .tr-row { border-top: 1px solid rgba(255,255,255,.07); }
.tr-line { display: flex; align-items: baseline; gap: 6px; }
.tr-name { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 150px; }
.tr-cls { opacity: .6; font-size: 11px; white-space: nowrap; }
.tr-spacer { flex: 1 1 auto; }
.tr-g { font-weight: 700; font-size: 11px; padding: 0 5px; border-radius: 3px; color: #10151b; }
.tr-wr { font-variant-numeric: tabular-nums; font-size: 11.5px; }
.tr-wr.up, .tr-chip.up { color: #7fd27f; }
.tr-wr.down, .tr-chip.down { color: #e88; }

/* diverging win-rate bar, centred on the 50% average */
.tr-bar { position: relative; height: 4px; margin: 3px 0 2px; border-radius: 2px; background: rgba(255,255,255,.1); }
.tr-bar::before { content: ''; position: absolute; left: 50%; top: -1px; bottom: -1px; width: 1px; background: rgba(255,255,255,.35); }
.tr-bar span { position: absolute; top: 0; bottom: 0; border-radius: 2px; }
.tr-bar span.up { background: #4caf72; }
.tr-bar span.down { background: #d9676b; }

.tr-chips { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; }
.tr-chip { font-size: 10.5px; padding: 0 5px; border-radius: 3px; background: rgba(255,255,255,.08); white-space: nowrap; }

/* grade-tier colours for the pills (match the .grade-badge tiers) */
.g-gold { background: #e0b54a; }
.g-blue { background: #6aa6e0; }
.g-green { background: #74cf78; }
.g-grey { background: #9aa7b4; }
</style>
